<template>
  <div class="omc-wrap">
    <span
      v-if="loading"
      class="loadingres mr-2 ml-2 spinner-border loading-spinner"
    ></span>

    <div
      v-if="hasEventData"
      class="omc-card"
      :class="isPast ? 'omc-card--past' : 'omc-card--future'"
    >
      <img
        v-if="event.data !== undefined && event.data.provider == 'gmail'"
        class="omc-provider-icon"
        width="15"
        src="@/assets/images/gmail.png"
        alt=""
      />
      <img
        v-if="event.data !== undefined && event.data.provider == 'ews'"
        class="omc-provider-icon"
        width="40"
        src="@/assets/images/outlook-calendar.png"
        alt=""
      />

      <div
        v-if="isPast"
        class="alert alert-warning omc-past-banner mb-0 rounded-0 border-0"
        role="alert"
      >
        <i class="icon-warning mr-2"></i>
        {{ $t("lang.opportunities.meeting_card_past_banner") }}
      </div>

      <Info
        :message="$t('lang.calendar.reschedule')"
        :color="'primary'"
        v-if="event.reschedule == '1'"
      ></Info>

      <div class="omc-card-body">
        <div class="omc-header">
          <div class="omc-company-block">
            <div
              v-if="companyInitials"
              class="omc-company-avatar"
            >
              {{ companyInitials }}
            </div>
            <div class="omc-company-info">
              <div class="omc-company-name-row">
                <span v-if="companyName" class="omc-company-name">{{
                  companyName
                }}</span>
                <small
                  v-if="
                    event.cancellation !== undefined &&
                    event.cancellation.canceler_type !== undefined &&
                    event.cancellation.canceler_type == 'host'
                  "
                  class="badge badge-danger ml-2"
                >
                  {{ $t("lang.opportunities.canceledbyclient") }}:
                  {{ event.cancellation.canceled_by }}
                </small>
              </div>
              <a
                v-if="companyWebsite && websiteHref"
                :href="websiteHref"
                target="_blank"
                rel="noopener noreferrer"
                class="omc-company-website"
              >
                {{ companyWebsite }}
                <i class="icon-external-link ml-1"></i>
              </a>
            </div>
          </div>
          <span
            v-if="relativeDaysLabel"
            class="omc-days-badge"
            :class="isPast ? 'omc-days-badge--past' : 'omc-days-badge--future'"
          >
            <i class="icon-clock mr-1"></i>
            {{ relativeDaysLabel }}
          </span>
        </div>

        <div class="omc-datetime-row">
          <div class="omc-datetime-col" v-if="formattedEventDate">
            <i class="icon-calendar omc-datetime-icon"></i>
            <div>
              <div class="omc-datetime-label">{{ $t("lang.column.date") }}</div>
              <div class="omc-datetime-value">{{ formattedEventDate }}</div>
            </div>
          </div>
          <div class="omc-datetime-col" v-if="formattedEventTime">
            <i class="icon-clock omc-datetime-icon"></i>
            <div>
              <div class="omc-datetime-label">{{ $t("lang.label.hour") }}</div>
              <div class="omc-datetime-value">{{ formattedEventTime }}</div>
            </div>
          </div>
        </div>

        <template v-if="event.location">
          <div
            v-if="showMeetingActions"
            class="omc-actions-row"
          >
            <a
              :href="meetingJoinUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-primary omc-join-btn"
            >
              <i class="icon-video-camera mr-2"></i>
              {{ $t("lang.label.joinTheMeeting") }}
            </a>
            <button
              type="button"
              class="omc-icon-btn"
              :class="{ 'omc-icon-btn--copied': copyLinkDone }"
              :title="
                copyLinkDone
                  ? $t('lang.label.copied')
                  : $t('lang.opportunities.meeting_card_copy_link')
              "
              @click="copyMeetingLink"
            >
              <i class="icon-copy"></i>
            </button>
            <button
              v-if="showEditMeetingDateButton"
              type="button"
              class="omc-icon-btn"
              :title="$t('lang.opportunities.meeting_card_edit_date_tooltip')"
              :disabled="loadingediting"
              @click="openEditDateModal"
            >
              <span
                v-if="loadingediting"
                class="spinner-border spinner-border-sm text-primary"
              ></span>
              <i v-else class="icon-calendar"></i>
            </button>
          </div>

          <div
            v-if="event.error_conference != null"
            class="omc-conference-warning"
          >
            <i class="icon-warning text-warning"></i>
            <span>{{ event.error_conference }}</span>
          </div>

          <div
            v-if="showPhoneCallButton"
            class="omc-phone-row"
          >
            <a
              :href="'tel:' + event.location.location"
              class="btn btn-primary omc-phone-btn"
            >
              <i class="icon-phone mr-2"></i>
              <small v-if="event.location.location">{{
                phoneWithSpaces(event.location.location)
              }}</small>
            </a>
          </div>
        </template>

        <div v-if="hasInvitees" class="omc-section">
          <div class="omc-section-title">
            <i class="icon-users mr-2"></i>
            {{ $t("lang.opportunities.meeting_card_participants") }}
          </div>
          <div
            v-for="invitee in orderedInvitees"
            :key="inviteeKey(invitee)"
            class="omc-participant"
          >
            <div class="omc-participant-left">
              <div class="omc-participant-avatar">
                {{ inviteeInitials(invitee) }}
              </div>
              <div class="omc-participant-details">
                <div class="omc-participant-name-row">
                  <span class="omc-participant-name">{{
                    inviteeDisplayName(invitee)
                  }}</span>
                  <Tooltip
                    :text="textToStatusInvitee(invitee)"
                    :width="'auto'"
                  >
                    <i
                      class="icon-circle omc-participant-status"
                      :class="getStatusInvitee(invitee)"
                    ></i>
                  </Tooltip>
                </div>
                <div v-if="invitee.email" class="omc-participant-email">
                  {{ invitee.email }}
                </div>
                <div
                  v-if="isProspectInvitee(invitee) && prospectPhone"
                  class="omc-participant-phone"
                >
                  {{ prospectPhone }}
                </div>
              </div>
            </div>
            <span
              v-if="inviteeRoleLabel(invitee)"
              class="omc-participant-tag"
            >
              {{ inviteeRoleLabel(invitee) }}
            </span>
          </div>
        </div>

        <div
          v-if="isPast && commentsText"
          class="omc-section"
        >
          <div class="omc-section-title">
            <i class="icon-comment mr-2"></i>
            {{ $t("lang.opportunities.meeting_card_comment") }}
          </div>
          <div class="omc-comments-box">{{ commentsText }}</div>
        </div>

        <div
          v-if="event.business_contact_data"
          class="omc-section omc-business-contact"
        >
          <div class="omc-section-title">
            {{ $t("lang.column.contacted_person") }}
          </div>
          <div class="d-flex align-items-center">
            <strong>
              {{ event.business_contact_data.first_name }}
              {{ event.business_contact_data.last_name }}
            </strong>
            <a
              v-if="event.business_contact_data.linkedinurl"
              class="ml-2"
              :href="event.business_contact_data.linkedinurl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="icon-linkedin color-ln"></i>
            </a>
          </div>
          <div v-if="event.business_contact_data.job">
            <small>{{ replaceChain(event.business_contact_data.job) }}</small>
          </div>
          <div>
            <small class="font-weight-bold text-primary">{{
              event.business_contact_data.email
            }}</small>
          </div>
        </div>

        <div
          v-if="showGestionAdmin"
          class="omc-section omc-admin-section"
        >
          <div class="omc-section-title omc-admin-title">
            <i class="icon-gear mr-2"></i>
            {{ $t("lang.opportunities.meeting_card_admin_section") }}
            <span class="omc-admin-badge">{{
              $t("lang.opportunities.meeting_card_admin_only")
            }}</span>
          </div>

          <div
            v-if="user.role_id == 1"
            class="omc-admin-row"
          >
            <div class="omc-admin-row-left">
              <i class="fa-solid fa-handshake omc-admin-row-icon"></i>
              <div>
                <div class="omc-admin-row-label">
                  {{ $t("lang.opportunities.meeting_card_booker_label") }}
                </div>
                <div class="omc-admin-row-value">
                  {{ event.bdr_name_winner || "—" }}
                </div>
              </div>
            </div>
            <button
              type="button"
              class="btn btn-link btn-sm omc-admin-link p-0"
              @click="openModalModifyMeetings()"
            >
              {{ $t("lang.opportunities.meeting_card_reassign") }}
            </button>
          </div>

          <Tooltip
            v-if="user.role_id == 1 && event.bdr_manual_assigned != null"
            class="omc-manual-assign-hint"
            :text="
              $t('lang.opportunities.bdrmanuallyassigned') +
              ' (' +
              event.bdr_manual_assigned +
              ')'
            "
            :width="'auto'"
          >
            <i class="icon-info text-primary"></i>
          </Tooltip>

          <div
            v-if="isBdrUser || isAdmin"
            class="omc-admin-row"
          >
            <div class="omc-admin-row-left">
              <i class="icon-user omc-admin-row-icon"></i>
              <div>
                <div class="omc-admin-row-label">
                  {{ $t("lang.label.attendance_status") }}
                </div>
                <div class="omc-admin-row-value">
                  {{
                    event.attendance_status
                      ? textToAttendanceStatus(event.attendance_status)
                      : $t("lang.opportunities.meeting_card_not_defined")
                  }}
                </div>
              </div>
            </div>
            <button
              type="button"
              class="btn btn-link btn-sm omc-admin-link p-0"
              @click="openModalModifyStatusAttendance('statusAttendanceModal')"
            >
              {{
                event.attendance_status
                  ? $t("lang.opportunities.meeting_card_edit_action")
                  : $t("lang.opportunities.meeting_card_define_action")
              }}
            </button>
          </div>
        </div>

        <span
          v-show="user.role_id != 4 && user.role_id != 3"
          v-if="user.role_id != 1 && event.bdr_name_winner"
          class="omc-bdr-name text-primary"
        >
          <b>{{ event.bdr_name_winner }}</b>
        </span>

        <div class="omc-footer">
          <small v-if="formattedCreatedAt">{{ formattedCreatedAt }}</small>
          <small v-if="timezoneLabel">{{ timezoneLabel }}</small>
        </div>
      </div>

      <div class="container mt-5" v-if="rescreate && rescreate.result">
        <div class="card">
          <div class="card-body">
            <h5 class="card-title">{{ rescreate.result.title }}</h5>
            <p class="card-text" v-html="rescreate.result.description"></p>
            <p><strong>Status:</strong> {{ rescreate.result.status }}</p>
            <p><strong>Start Time:</strong> {{ start_time }}</p>
            <p><strong>End Time:</strong>{{ end_time }}</p>
            <p>
              <strong>Organizer:</strong> {{ rescreate.result.organizer_name }}
            </p>
            <p>
              <strong>Organizer Email:</strong>
              {{ rescreate.result.organizer_email }}
            </p>
            <p>
              <strong>Meeting URL:</strong>
              <a
                :href="rescreate.result.conferencing.details.url"
                target="_blank"
                >{{ rescreate.result.conferencing.details.url }}</a
              >
            </p>
            <p><strong>Participants:</strong></p>
            <ul>
              <li
                v-for="(participant, pIdx) in rescreate.result.participants"
                :key="pIdx"
              >
                {{ participant.email }} ({{ participant.status }})
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="row" v-if="rescreate && !rescreate.result">
        <div class="badge badge-danger">
          <strong>{{ rescreate }}</strong>
        </div>
      </div>
      <div
        class="row"
        v-if="
          eventCalendly &&
          eventCalendly.notsync == '1' &&
          eventCalendly.data
        "
      >
        <div class="col-12">
          <button
            :disabled="
              loadingcreate || eventCalendly.data.activated_spark != '1'
            "
            class="btn btn-sm btn-primary mt-4"
            @click="createEvent(eventCalendly)"
            v-if="!rescreate"
          >
            <i class="icon-send"></i> Create event
            <span
              class="loadingres mr-2 ml-2 spinner-border loading-spinner"
              v-if="loadingcreate"
            ></span>
          </button>
        </div>
        <div class="col-12">
          <button
            :disabled="
              loadingcreate ||
              eventCalendly.data.activated_spark == '1' ||
              eventCalendly.data.activated == '1'
            "
            class="btn btn-sm btn-primary mt-1"
            @click="createEvent(eventCalendly)"
            v-if="!rescreate"
          >
            <i class="icon-send"></i> Create event using prospector
            <span
              class="loadingres mr-2 ml-2 spinner-border loading-spinner"
              v-if="loadingcreate"
            ></span>
          </button>
        </div>
      </div>
    </div>

    <modal
      :resizable="false"
      :scrollable="true"
      :max-width="480"
      height="auto"
      :size="'480px'"
      style="z-index: 10"
      class="modal-preview p-4"
      :name="editMeetingDateModalName"
      @closed="onEditDateModalClosed"
    >
      <div v-if="event && editingDate" class="p-4">
        <h5 class="mb-3">
          {{ $t("lang.opportunities.meeting_card_edit_date_modal_title") }}
        </h5>
        <div class="d-flex align-items-center flex-wrap">
          <i class="icon-calendar text-primary mr-2"></i>
          <date-picker
            :id="'endDatePicker-' + meeting_id"
            style="width: 220px; position: relative"
            class="d-inline p-0 text-center"
            v-model="event.start_time"
            :config="datePickerConfigEndDate"
            placeholder="Event Date"
          ></date-picker>
          <strong v-if="timezoneLabel" class="text-secondary ml-2">{{
            timezoneLabel
          }}</strong>
        </div>
        <p class="text-danger small mt-3 mb-0">
          {{ $t("lang.opportunities.meeting_card_warning")
          }}<i class="icon-warning text-warning"></i> :
          {{ $t("lang.calendar.modifyeventrisk") }}
        </p>
        <div class="d-flex justify-content-end mt-4">
          <button
            type="button"
            class="btn btn-secondary mr-2"
            @click="closeEditDateModal"
          >
            {{ $t("lang.button.cancel") }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="confirmEditDate"
          >
            {{ $t("lang.label.edit") }}
          </button>
        </div>
      </div>
    </modal>

    <modal
      :resizable="false"
      :scrollable="false"
      :max-width="400"
      height="auto"
      :size="'440px'"
      style="z-index: 10"
      class="modal-preview p-4"
      name="statusAttendanceModal"
      @before-open="onModalBeforeOpen"
      @opened="onModalOpened"
    >
      <div class="p-4">
        <h5 class="mb-4">{{ $t("lang.label.set_attendance_status") }}</h5>
        <div class="form-group">
          <label for="attendanceStatusSelect">{{
            $t("lang.label.attendance_status")
          }}</label>
          <select
            id="attendanceStatusSelect"
            class="form-control"
            v-model="selectedAttendanceStatus"
          >
            <option value="">{{ $t("lang.label.select_a_status") }}</option>
            <option value="confirmed_attendance">
              {{ $t("lang.label.confirmed_attendance") }}
            </option>
            <option value="rescheduled_meeting">
              {{ $t("lang.label.rescheduled_meeting") }}
            </option>
            <option value="cancelled_meeting">
              {{ $t("lang.label.cancelled_meeting") }}
            </option>
            <option value="unable_to_reach">
              {{ $t("lang.label.unable_to_reach") }}
            </option>
            <option value="undefined_meeting">
              {{ $t("lang.label.undefined_meeting") }}
            </option>
          </select>
        </div>
        <div class="d-flex justify-content-end mt-3">
          <button
            class="btn btn-secondary mr-2"
            @click="closeAttendanceModal"
            :disabled="loadingAttendanceStatus"
          >
            {{ $t("lang.button.cancel") }}
          </button>
          <button
            class="btn btn-primary"
            @click="saveAttendanceStatus"
            :disabled="!selectedAttendanceStatus || loadingAttendanceStatus"
          >
            <span
              v-if="loadingAttendanceStatus"
              class="spinner-border spinner-border-sm mr-2"
            ></span>
            {{ $t("lang.button.save") }}
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { calendlyService, clientsService, linksService } from "@/_services";
import moment from "moment";
import { mapState, mapActions } from "vuex";
import Tooltip from "@/components/Common/Tooltip.vue";
import Modal from "@/components/Common/Modal.vue";
import Info from "@/components/Common/Info.vue";

export default {
  components: { Tooltip, Info, Modal },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    eventCalendly: {
      type: Object,
      default: null,
    },
    meeting_id: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      eventdata: false,
      prev_start_time: false,
      loadingediting: false,
      editingDate: false,
      allowsDateEdit: false,
      eventBeforeEdit: null,
      start_time: "",
      end_time: "",
      loadingcreate: false,
      rescreate: false,
      event: false,
      loading: true,
      datePickerConfigEndDate: {
        format: "YYYY-MM-DD H:mm",
        useCurrent: false,
        minDate: moment().endOf("day"),
      },
      selectedAttendanceStatus: "",
      loadingAttendanceStatus: false,
      modalMeetingId: null,
      copyLinkDone: false,
      copyLinkTimeout: null,
    };
  },
  created() {
    if (this.id == undefined && this.eventCalendly) {
      this.event = this.eventCalendly;
      this.updateAllowsDateEdit(this.event);
      this.loading = false;
    } else if (this.id != undefined) {
      this.getEvent();
    } else {
      this.loading = false;
    }
  },
  beforeDestroy() {
    if (this.copyLinkTimeout) {
      clearTimeout(this.copyLinkTimeout);
    }
  },
  computed: {
    textToAttendanceStatus() {
      return (attendanceStatus) => {
        switch (attendanceStatus) {
          case "confirmed_attendance":
            return this.$t("lang.label.confirmed_attendance");
          case "rescheduled_meeting":
            return this.$t("lang.label.rescheduled_meeting");
          case "cancelled_meeting":
            return this.$t("lang.label.cancelled_meeting");
          case "unable_to_reach":
            return this.$t("lang.label.unable_to_reach");
          case "undefined_meeting":
            return this.$t("lang.label.undefined_meeting");
          default:
            return "";
        }
      };
    },
    ...mapState({
      opportunity: (state) => state.opportunities.selected.data,
      user: (state) => state.authentication.user,
    }),
    textToStatusInvitee() {
      return (invitee) => {
        if (!invitee.status == undefined) {
          return "";
        }
        if (invitee.status == "canceled" || invitee.status == "cancelled") {
          return this.$t("lang.opportunities.meeting_cancelled");
        }
        if (invitee.status == "confirmed" || invitee.status == "active") {
          return this.$t("lang.opportunities.invite_accepted");
        }
        if (invitee.status == "maybe") {
          return this.$t("lang.opportunities.invite_pending");
        }
        return "";
      };
    },
    isBdrUser() {
      return this.user && this.user.booker ? true : false;
    },
    isAdmin() {
      return this.user && this.user.role_id == 1 ? true : false;
    },
    phoneWithSpaces() {
      return (value) => {
        let phone = "";
        if (value !== null && value !== undefined && value.length > 0) {
          value
            .replace(/\s+/g, "")
            .split("")
            .forEach((digit, i) => {
              if (i % 2) {
                phone += digit;
              } else {
                phone += " " + digit;
              }
            });
          phone = "+" + phone;
        }
        return phone;
      };
    },
    hasEventData() {
      return (
        this.event &&
        this.event.name !== undefined &&
        this.event.name !== null
      );
    },
    parsedStartDate() {
      if (!this.event || !this.event.start_time) return null;
      const raw = String(this.event.start_time).trim();
      const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
      const date = new Date(normalized);
      return isNaN(date.getTime()) ? null : date;
    },
    isPast() {
      const date = this.parsedStartDate;
      if (!date) return false;
      return date.getTime() < Date.now();
    },
    daysDifference() {
      const date = this.parsedStartDate;
      if (!date) return null;
      const startDay = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );
      const now = new Date();
      const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffMs = startDay.getTime() - nowDay.getTime();
      return Math.round(diffMs / (1000 * 60 * 60 * 24));
    },
    relativeDaysLabel() {
      const days = this.daysDifference;
      if (days === null || days === undefined) return "";
      if (this.isPast) {
        const n = Math.abs(days);
        const key =
          n > 1
            ? "lang.opportunities.meeting_card_past_days_plural"
            : "lang.opportunities.meeting_card_past_days";
        return this.$t(key, { n });
      }
      if (days === 0) return this.$t("lang.label.today");
      const key =
        days > 1
          ? "lang.opportunities.meeting_card_in_days_plural"
          : "lang.opportunities.meeting_card_in_days";
      return this.$t(key, { n: days });
    },
    prospectInvitee() {
      if (!this.event || !Array.isArray(this.event.invitees)) return null;
      return (
        this.event.invitees.find((inv) => this.isProspectInvitee(inv)) || null
      );
    },
    internalInvitee() {
      return this.resolvedInternalInvitee;
    },
    resolvedInternalInvitee() {
      if (!this.event || !Array.isArray(this.event.invitees)) return null;
      const candidates = this.event.invitees.filter(
        (inv) => !this.isProspectInvitee(inv) && this.hasOnlyCommentsQa(inv)
      );
      if (candidates.length === 0) return null;
      if (candidates.length === 1) return candidates[0];

      const internalDomains = ["oliverlist.com", "aivoni.fr"];
      const byDomain = candidates.find((inv) => {
        const domain = (inv.email || "").split("@")[1].toLowerCase() || "";
        return internalDomains.some(
          (d) => domain === d || domain.endsWith("." + d)
        );
      });
      if (byDomain) return byDomain;

      const userEmail = (this.user && this.user.email || "")
        .trim()
        .toLowerCase();
      if (userEmail) {
        const match = candidates.find(
          (inv) => (inv.email || "").trim().toLowerCase() === userEmail
        );
        if (match) return match;
      }

      return null;
    },
    companyName() {
      const inv = this.prospectInvitee;
      if (!inv) return "";
      return this.getQaAnswer(inv, "Company name") || "";
    },
    companyWebsite() {
      const inv = this.prospectInvitee;
      if (!inv) return "";
      return this.getQaAnswer(inv, "Website") || "";
    },
    companyInitials() {
      const name = this.companyName;
      if (!name || typeof name !== "string") return "";
      return name
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    websiteHref() {
      const site = this.companyWebsite;
      if (!site || typeof site !== "string") return null;
      const trimmed = site.trim();
      if (!trimmed) return null;
      if (/^https?:\/\//i.test(trimmed)) return trimmed;
      return "https://" + trimmed;
    },
    formattedEventDate() {
      const date = this.parsedStartDate;
      if (!date) return "";
      const formatted = date.toLocaleDateString(this.i18nDateLocale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      if (!formatted) return "";
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },
    formattedEventTime() {
      const date = this.parsedStartDate;
      if (!date) return "";
      const time = date.toLocaleTimeString(this.i18nDateLocale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      const zone = this.timezoneZoneName;
      return zone ? `${time} (${zone})` : time;
    },
    timezoneZoneName() {
      if (
        !this.event ||
        !this.event.timezone ||
        this.event.timezone === "" ||
        this.event.timezone === null
      ) {
        return "";
      }
      return this.event.timezone.zone_name || "";
    },
    timezoneLabel() {
      const zone = this.timezoneZoneName;
      return zone
        ? this.$t("lang.opportunities.meeting_card_timezone", { zone })
        : "";
    },
    formattedCreatedAt() {
      if (!this.event || !this.event.created_at) return "";
      const raw = String(this.event.created_at).trim();
      const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
      const date = new Date(normalized);
      if (isNaN(date.getTime())) return "";
      const dayMonth = date.toLocaleDateString(this.i18nDateLocale, {
        day: "numeric",
        month: "long",
      });
      const time = date.toLocaleTimeString(this.i18nDateLocale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      return this.$t("lang.opportunities.meeting_card_created_at", {
        date: dayMonth,
        time,
      });
    },
    hasInvitees() {
      return (
        this.event &&
        Array.isArray(this.event.invitees) &&
        this.event.invitees.length > 0
      );
    },
    orderedInvitees() {
      if (!this.hasInvitees) return [];
      const list = [...this.event.invitees];
      list.sort((a, b) => {
        const rank = (inv) => {
          if (this.isProspectInvitee(inv)) return 0;
          if (this.isInternalInvitee(inv)) return 1;
          return 2;
        };
        return rank(a) - rank(b);
      });
      return list;
    },
    prospectPhone() {
      const inv = this.prospectInvitee;
      if (!inv) return "";
      const fromQa = this.getQaAnswer(inv, "Phone");
      if (fromQa) return fromQa;
      if (inv.phone) return inv.phone;
      return "";
    },
    commentsText() {
      const inv = this.prospectInvitee;
      if (!inv) return "";
      const answer = this.getQaAnswer(inv, "Comments");
      return answer != null && answer !== undefined ? String(answer) : "";
    },
    meetingJoinUrl() {
      if (!this.event || !this.event.location) return "";
      const url = this.event.location.join_url;
      if (url === undefined || url === null) return "";
      return String(url).trim();
    },
    showMeetingActions() {
      return !this.isPast && this.meetingJoinUrl !== "";
    },
    showPhoneCallButton() {
      if (!this.event || !this.event.location) return false;
      return this.meetingJoinUrl === "";
    },
    showGestionAdmin() {
      return this.isAdmin || this.isBdrUser;
    },
    showEditMeetingDateButton() {
      if (this.isPast) return false;
      return this.allowsDateEdit;
    },
    editMeetingDateModalName() {
      const key = this.meeting_id || this.id || "default";
      return `editMeetingDateModal-${key}`;
    },
    i18nDateLocale() {
      const locale = (this.$i18n && this.$i18n.locale) || "fr_FR";
      const map = {
        fr_FR: "fr-FR",
        es_ES: "es-ES",
        en_US: "en-US",
        it_IT: "it-IT",
      };
      return map[locale] || "fr-FR";
    },
  },
  methods: {
    updateAllowsDateEdit(event) {
      if (!event) {
        this.allowsDateEdit = false;
        return;
      }
      this.allowsDateEdit =
        event.calendar_event == undefined &&
        this.isValidCalendarEventId(event.calendar_event_id);
    },
    isValidCalendarEventId(calendarEventId) {
      if (calendarEventId == null || calendarEventId === "") return false;
      const idStr = String(calendarEventId).trim();
      if (!/^\d+$/.test(idStr)) return false;
      if (this.id != null && idStr === String(this.id).trim()) return false;
      return true;
    },
    getQaAnswer(invitee, question) {
      if (!invitee || !Array.isArray(invitee.questions_and_answers)) {
        return "";
      }
      const item = invitee.questions_and_answers.find(
        (q) => q && q.question === question
      );
      if (!item || item.answer == null || item.answer === undefined) {
        return "";
      }
      return String(item.answer);
    },
    hasQaAnswer(invitee, question) {
      const value = this.getQaAnswer(invitee, question);
      return value !== "" && String(value).trim() !== "";
    },
    isProspectInvitee(invitee) {
      if (!invitee) return false;
      return (
        this.hasQaAnswer(invitee, "Company name") &&
        this.hasQaAnswer(invitee, "Phone")
      );
    },
    hasOnlyCommentsQa(invitee) {
      if (!invitee || !Array.isArray(invitee.questions_and_answers)) {
        return false;
      }
      const qa = invitee.questions_and_answers;
      if (!qa.length) return false;
      return qa.every((q) => q && q.question === "Comments");
    },
    isInternalInvitee(invitee) {
      const internal = this.resolvedInternalInvitee;
      if (!internal || !invitee) return false;
      return (
        (invitee.email || "").trim().toLowerCase() ===
        (internal.email || "").trim().toLowerCase()
      );
    },
    inviteeRoleLabel(invitee) {
      if (this.isProspectInvitee(invitee)) {
        return this.$t("lang.label.prospect");
      }
      if (this.isInternalInvitee(invitee)) {
        return this.$t("lang.label.client_team_you_badge");
      }
      return "";
    },
    inviteeDisplayName(invitee) {
      if (!invitee) return "";
      if (invitee.name) return invitee.name;
      const parts = [invitee.first_name, invitee.last_name].filter(Boolean);
      return parts.join(" ") || invitee.email || "";
    },
    inviteeInitials(invitee) {
      const name = this.inviteeDisplayName(invitee);
      if (!name) return "?";
      return name
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    },
    inviteeKey(invitee) {
      return (invitee && (invitee.email || invitee.name)) || Math.random();
    },
    copyMeetingLink() {
      const url = this.meetingJoinUrl;
      if (!url) return;
      const done = () => {
        this.copyLinkDone = true;
        if (this.copyLinkTimeout) clearTimeout(this.copyLinkTimeout);
        this.copyLinkTimeout = setTimeout(() => {
          this.copyLinkDone = false;
        }, 2000);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(url)
          .then(done)
          .catch(() => {
            if (this.copyToClipboardFallback(url)) done();
          });
        return;
      }
      if (this.copyToClipboardFallback(url)) done();
    },
    copyToClipboardFallback(text) {
      if (window.clipboardData && window.clipboardData.setData) {
        return window.clipboardData.setData("Text", text);
      }
      if (
        document.queryCommandSupported &&
        document.queryCommandSupported("copy")
      ) {
        const textarea = document.createElement("textarea");
        textarea.textContent = text;
        textarea.style.position = "fixed";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          return document.execCommand("copy");
        } catch (ex) {
          console.warn("Copy to clipboard failed.", ex);
          return false;
        } finally {
          document.body.removeChild(textarea);
        }
      }
      return false;
    },
    openEditDateModal() {
      if (!this.showEditMeetingDateButton || this.loadingediting) return;
      this.openEdit();
    },
    onEditDateModalClosed() {
      if (!this.editingDate && !this.eventBeforeEdit) return;
      this.closeEditDateModal(false);
    },
    closeEditDateModal(hideModal = true) {
      this.editingDate = false;
      this.loadingediting = false;
      if (this.eventBeforeEdit) {
        this.event = this.eventBeforeEdit;
        this.eventBeforeEdit = null;
      }
      if (hideModal) {
        this.$modal.hide(this.editMeetingDateModalName);
      }
    },
    confirmEditDate() {
      if (!this.event || !this.event.start_time) return;
      this.changeEventDate();
    },
    openEdit() {
      if (!this.event) return;
      const calendarEventId = this.event.calendar_event_id;
      if (!this.isValidCalendarEventId(calendarEventId)) {
        this.$swal({
          type: "error",
          title: this.$t("lang.label.error"),
          text: this.$t(
            "lang.opportunities.meeting_card_error_missing_calendar_id"
          ),
          confirmButtonText: this.$t("lang.button.ok"),
        });
        return;
      }
      this.loadingediting = true;
      this.eventBeforeEdit = this.event;
      clientsService
        .getAllCalendarNylas({ event_id: calendarEventId })
        .then((result) => {
          if (!result || !result[0]) {
            this.loadingediting = false;
            this.eventBeforeEdit = null;
            this.$swal({
              type: "error",
              title: this.$t("lang.label.error"),
              text: this.$t(
                "lang.opportunities.meeting_card_error_calendar_not_found"
              ),
              confirmButtonText: this.$t("lang.button.ok"),
            });
            return;
          }
          this.eventdata = result[0];
          this.prev_start_time = this.eventdata.start_time;
          this.eventdata.invitees = this.event.invitees;
          this.event = this.eventdata;
          this.editingDate = true;
          this.loadingediting = false;
          this.$nextTick(() => {
            this.$modal.show(this.editMeetingDateModalName);
          });
        })
        .catch(() => {
          this.loadingediting = false;
          this.eventBeforeEdit = null;
          this.$swal({
            type: "error",
            title: this.$t("lang.label.error"),
            text: this.$t("lang.opportunities.meeting_card_error_calendar_load"),
            confirmButtonText: this.$t("lang.button.ok"),
          });
        });
    },
    changeEventDate() {
      if (this.prev_start_time == this.event.start_time) {
        this.closeEditDateModal(true);
        return;
      }
      this.$swal({
        type: "warning",
        title: this.$t("lang.calendar.modifysure"),
        text: this.$t("lang.calendar.modifyevent"),
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33a2c",
        confirmButtonText: this.$t("lang.button.ok"),
      }).then((result) => {
        if (result.value) {
          this.eventBeforeEdit = null;
          this.createEvent(this.event);
          this.editingDate = false;
          this.allowsDateEdit = false;
          this.closeEditDateModal(true);
        }
      });
    },
    openModalModifyMeetings() {
      this.$emit("open-modal-mod-meet", {
        meetingId: this.meeting_id,
        bdrTaskName: this.event.bdr_name_winner,
        bdrTaskId: this.event.bdr_notification_id,
      });
    },
    onModalBeforeOpen(event) {
      const meetingId = this.meeting_id || this.id || null;
      if (meetingId) {
        event.params = event.params || {};
        event.params.meetingId = meetingId;
        if (!window.modalMeetingIds) {
          window.modalMeetingIds = {};
        }
        window.modalMeetingIds["statusAttendanceModal"] = meetingId;
        this.modalMeetingId = meetingId;
        console.log("onModalBeforeOpen - meeting_id guardado:", meetingId);
      }
    },
    onModalOpened(event) {
      let meetingId = null;
      if (event && event.params && event.params.meetingId) {
        meetingId = event.params.meetingId;
      } else if (
        window.modalMeetingIds &&
        window.modalMeetingIds["statusAttendanceModal"]
      ) {
        meetingId = window.modalMeetingIds["statusAttendanceModal"];
      }
      if (meetingId) {
        this.modalMeetingId = meetingId;
        console.log(
          "onModalOpened - meeting_id desde params/storage:",
          meetingId
        );
      }
    },
    openModalModifyStatusAttendance(name) {
      const meetingId = this.meeting_id || this.id || null;
      this.modalMeetingId = meetingId;
      if (!window.modalMeetingIds) {
        window.modalMeetingIds = {};
      }
      if (meetingId) {
        window.modalMeetingIds["statusAttendanceModal"] = meetingId;
      }
      this.selectedAttendanceStatus = "";
      this.$modal.show(name, { meetingId: meetingId });
    },
    closeAttendanceModal() {
      this.$modal.hide("statusAttendanceModal");
      this.selectedAttendanceStatus = "";
      this.modalMeetingId = null;
      if (window.modalMeetingIds) {
        delete window.modalMeetingIds["statusAttendanceModal"];
      }
    },
    saveAttendanceStatus() {
      if (!this.selectedAttendanceStatus) {
        this.$swal({
          type: "warning",
          title: this.$t("lang.opportunities.meeting_card_warning"),
          text: this.$t(
            "lang.opportunities.meeting_card_select_attendance_status"
          ),
          confirmButtonText: this.$t("lang.button.ok"),
        });
        return;
      }

      let meetingIdToUse = null;

      if (
        window.modalMeetingIds &&
        window.modalMeetingIds["statusAttendanceModal"]
      ) {
        meetingIdToUse = window.modalMeetingIds["statusAttendanceModal"];
      } else if (this.modalMeetingId) {
        meetingIdToUse = this.modalMeetingId;
      } else {
        meetingIdToUse = this.meeting_id || this.id;
      }

      if (!meetingIdToUse) {
        console.error("No se encontró meeting_id. Valores disponibles:", {
          meeting_id_prop: this.meeting_id,
          id_prop: this.id,
          modalMeetingId: this.modalMeetingId,
          window_modalMeetingIds: window.modalMeetingIds,
          event: this.event,
        });
        this.$swal({
          type: "error",
          title: this.$t("lang.label.error"),
          text: this.$t("lang.opportunities.meeting_card_meeting_id_missing"),
          confirmButtonText: this.$t("lang.button.ok"),
        });
        return;
      }

      this.loadingAttendanceStatus = true;
      linksService
        .updateAttendanceStatus(meetingIdToUse, this.selectedAttendanceStatus)
        .then((response) => {
          if (this.event && typeof this.event === "object") {
            this.event.attendance_status = this.selectedAttendanceStatus;
          }
          this.loadingAttendanceStatus = false;
          this.$swal({
            type: "success",
            title: this.$t("lang.taskGeneration.successful"),
            text: this.$t("lang.label.status_saved"),
            confirmButtonText: this.$t("lang.button.ok"),
          }).then(() => {
            this.closeAttendanceModal();
          });
        })
        .catch((error) => {
          this.loadingAttendanceStatus = false;
          this.$swal({
            type: "error",
            title: this.$t("lang.label.error"),
            text:
              error.message ||
              this.$t("lang.opportunities.meeting_card_attendance_save_error"),
            confirmButtonText: this.$t("lang.button.ok"),
          });
        });
    },
    ...mapActions("opportunities", {
      setLoadEvent: "setLoadEvent",
    }),
    isReschedule() {
      if (this.event.remark !== null)
        return this.event.remark.includes("NOTSYNC-RESCHEDULE");

      return false;
    },
    createEvent(event) {
      this.rescreate = false;

      let arr = {
        calendar_event_id: event.data.calendar_event_id,
        start_time: event.start_time,
        end_time: event.end_time,
        calendar_client_nylas_id: event.data.calendar_client_nylas_id,
      };
      this.loadingcreate = true;
      clientsService.regenerateEvent(arr).then((res) => {
        this.loadingcreate = false;
        if (res.result == undefined) {
          res.result = res;
        }

        if (!res.result.error) {
          this.rescreate = res;
          this.start_time = new moment.unix(res.result.when.start_time).format(
            "YYYY-MM-DD HH:mm:ss"
          );
          this.end_time = new moment.unix(res.result.when.end_time).format(
            "YYYY-MM-DD HH:mm:ss"
          );
          this.$forceUpdate();
        } else {
          if (res.result.error.message !== undefined) {
            this.rescreate = res.result.error.message;
          }
          if (res.result.error_description !== undefined) {
            this.rescreate = res.result.error_description;
          }
        }
      });
    },
    getEvent() {
      calendlyService.getEvent(this.id, this.meeting_id).then((result) => {
        this.loading = false;
        this.event = result;
        this.updateAllowsDateEdit(result);
        this.event.business_contact_data = this.event.event_type.includes(
          "calendly"
        )
          ? ""
          : JSON.parse(this.event.business_contact_data);

        if (this.opportunity != undefined) {
          this.opportunity.link.event = this.event;
          this.setLoadEvent(true);
        }
      });
    },
    replaceChain(chain) {
      if (chain != null) {
        var search = "&amp;";
        var str = chain;
        str = str.replace(new RegExp(search, "g"), "&");
        return str;
      }
    },
    getStatusInvitee(invitee) {
      if (invitee.status == undefined) {
        return "text-secondary";
      }
      if (invitee.status == "noreply") {
        return "text-secondary";
      }
      if (invitee.status == "no") {
        return "text-danger";
      }
      if (invitee.status == "canceled" || invitee.status == "cancelled") {
        return "text-danger";
      }
      if (invitee.status == "yes") {
        return "text-success";
      }
      if (invitee.status == "confirmed" || invitee.status == "accepted") {
        return "text-success";
      }
      if (invitee.status == "maybe") {
        return "text-warning";
      }
      if (invitee.status == "active") {
        return "text-success";
      }

      return "text-white";
    },
  },
};
</script>

<style scoped>
.omc-wrap {
  width: 100%;
}

.omc-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.omc-card--future {
  border: 2px solid #10b981;
}

.omc-card--past {
  border: 2px solid #f59e0b;
}

.omc-provider-icon {
  display: block;
  margin: 8px 0 0 12px;
}

.omc-past-banner {
  font-size: 13px;
  font-weight: 500;
  padding: 10px 16px;
  background-color: #fef3c7;
  color: #92400e;
  border-bottom: 1px solid #fde68a !important;
}

.omc-card-body {
  padding: 14px 16px 12px;
}

.omc-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.omc-company-block {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.omc-company-avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #eeeefc 0%, #f7f7fe 100%);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  color: #5054eb;
  flex-shrink: 0;
}

.omc-company-info {
  min-width: 0;
}

.omc-company-name {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  display: block;
}

.omc-company-website {
  font-size: 12px;
  color: #5054eb;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.omc-company-website:hover {
  text-decoration: underline;
}

.omc-days-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
}

.omc-days-badge--future {
  background: #d1fae5;
  color: #065f46;
}

.omc-days-badge--past {
  background: #fef3c7;
  color: #92400e;
}

.omc-datetime-row {
  display: flex;
  gap: 20px;
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f4f4f7;
}

.omc-datetime-col {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.omc-datetime-icon {
  color: #6b7280;
  font-size: 16px;
  margin-top: 2px;
}

.omc-datetime-label {
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.omc-datetime-value {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
}

.omc-actions-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.omc-join-btn {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  border-radius: 8px;
  padding: 10px 14px;
}

.omc-icon-btn {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  flex-shrink: 0;
  padding: 0;
}

.omc-icon-btn:hover:not(:disabled) {
  border-color: #5054eb;
  color: #5054eb;
  background: #f7f7fe;
}

.omc-icon-btn:focus,
.omc-icon-btn:active {
  outline: none;
  box-shadow: none;
  background: #fff;
  color: #6b7280;
  border-color: #e5e7eb;
}

.omc-icon-btn--copied {
  border-color: #10b981;
  color: #10b981;
  background: #ecfdf5;
}

.omc-icon-btn--copied:focus,
.omc-icon-btn--copied:active {
  border-color: #10b981;
  color: #10b981;
  background: #ecfdf5;
}

.omc-icon-btn:disabled {
  opacity: 1;
  cursor: wait;
  background: #fff;
  border-color: #e5e7eb;
}

.omc-conference-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #92400e;
}

.omc-phone-row {
  margin-bottom: 12px;
}

.omc-phone-btn {
  border-radius: 8px;
}

.omc-section {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #f4f4f7;
}

.omc-section-title {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.4px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.omc-participant {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 0;
}

.omc-participant + .omc-participant {
  border-top: 1px solid #fafafb;
}

.omc-participant-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
}

.omc-participant-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f4f4f7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
}

.omc-participant-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.omc-participant-status {
  font-size: 10px;
  line-height: 1;
  flex-shrink: 0;
}

.omc-participant-name {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  min-width: 0;
}

.omc-participant-email,
.omc-participant-phone {
  font-size: 12px;
  color: #6b7280;
}

.omc-participant-tag {
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 5px;
  background: #f4f4f7;
  color: #6b7280;
  flex-shrink: 0;
}

.omc-comments-box {
  background: #fafafb;
  border: 1px solid #f4f4f7;
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.omc-business-contact {
  background: #fafafb;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #f4f4f7;
}

.omc-admin-section {
  background: #fafafb;
  border-radius: 8px;
  padding: 12px;
  margin-top: 14px;
  border: 1px solid #f4f4f7;
}

.omc-admin-title {
  flex-wrap: wrap;
  gap: 6px;
}

.omc-admin-badge {
  font-size: 9px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  background: #ede9fe;
  color: #5b21b6;
  letter-spacing: 0.3px;
}

.omc-admin-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #f4f4f7;
  border-radius: 8px;
  margin-bottom: 8px;
}

.omc-admin-row:last-child {
  margin-bottom: 0;
}

.omc-admin-row-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.omc-admin-row-icon {
  font-size: 18px;
  color: #6b7280;
}

.omc-admin-row-label {
  font-size: 10px;
  color: #6b7280;
  font-weight: 500;
}

.omc-admin-row-value {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
}

.omc-admin-link {
  font-size: 12px;
  font-weight: 500;
  color: #5054eb !important;
  white-space: nowrap;
}

.omc-manual-assign-hint {
  display: block;
  margin: -4px 0 8px 4px;
}

.omc-bdr-name {
  display: block;
  margin-top: 8px;
  font-size: 13px;
}

.omc-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #f4f4f7;
  font-size: 11px;
  color: #9ca3af;
}
</style>
